/* ========================================
   PERFORMANCE MONITORING UTILITY
   OPTIMIZED FOR MOBILE PERFORMANCE
   ======================================== */

class PerformanceMonitor {
  constructor() {
    this.metrics = {};
    this.isMobile = this.detectMobile();
    this.isLowEndDevice = this.detectLowEndDevice();
    this.init();
  }

  init() {
    // Only initialize performance monitoring on non-low-end devices
    if (!this.isLowEndDevice) {
      this.observePerformance();
      this.observeImages();
      this.observeLayoutShifts();
    }
    
    // Always initialize basic metrics
    this.captureBasicMetrics();
  }

  detectMobile() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  }

  detectLowEndDevice() {
    // Detect low-end devices based on hardware concurrency and memory
    const cores = navigator.hardwareConcurrency || 1;
    const memory = navigator.deviceMemory || 1;
    
    return cores <= 2 || memory <= 2 || this.isMobile;
  }

  observePerformance() {
    if ('PerformanceObserver' in window) {
      // Observe navigation timing
      try {
        const navigationObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            if (entry.entryType === 'navigation') {
              this.metrics.navigation = {
                domContentLoaded: entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart,
                loadComplete: entry.loadEventEnd - entry.loadEventStart,
                totalTime: entry.loadEventEnd - entry.fetchStart
              };
            }
          });
        });
        navigationObserver.observe({ entryTypes: ['navigation'] });
      } catch (e) {
        console.warn('Navigation timing observation failed:', e);
      }

      // Observe paint timing
      try {
        const paintObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            if (entry.name === 'first-paint') {
              this.metrics.firstPaint = entry.startTime;
            }
            if (entry.name === 'first-contentful-paint') {
              this.metrics.firstContentfulPaint = entry.startTime;
            }
          });
        });
        paintObserver.observe({ entryTypes: ['paint'] });
      } catch (e) {
        console.warn('Paint timing observation failed:', e);
      }
    }
  }

  observeImages() {
    if (!this.isLowEndDevice) {
      // Lazy load images for better performance
      this.setupLazyLoading();
    }
  }

  setupLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.classList.remove('lazy');
              observer.unobserve(img);
            }
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      // Observe all lazy images
      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  }

  observeLayoutShifts() {
    if ('PerformanceObserver' in window && !this.isLowEndDevice) {
      try {
        const layoutShiftObserver = new PerformanceObserver((list) => {
          let cumulativeLayoutShift = 0;
          list.getEntries().forEach(entry => {
            if (!entry.hadRecentInput) {
              cumulativeLayoutShift += entry.value;
            }
          });
          this.metrics.cumulativeLayoutShift = cumulativeLayoutShift;
        });
        layoutShiftObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        console.warn('Layout shift observation failed:', e);
      }
    }
  }

  captureBasicMetrics() {
    // Capture basic performance metrics
    if (window.performance && window.performance.timing) {
      const timing = window.performance.timing;
      this.metrics.pageLoad = timing.loadEventEnd - timing.navigationStart;
      this.metrics.domReady = timing.domContentLoadedEventEnd - timing.navigationStart;
    }

    // Capture device capabilities
    this.metrics.deviceInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      cores: navigator.hardwareConcurrency || 'unknown',
      memory: navigator.deviceMemory || 'unknown',
      connection: navigator.connection ? navigator.connection.effectiveType : 'unknown',
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };
  }

  optimizeForDevice() {
    if (this.isLowEndDevice) {
      // Reduce animations and effects for low-end devices
      this.reduceAnimations();
      this.optimizeImages();
      this.reduceComplexity();
    }
  }

  reduceAnimations() {
    // Add CSS class for reduced animations
    document.documentElement.classList.add('reduced-motion');
    
    // Disable complex animations
    const style = document.createElement('style');
    style.textContent = `
      .reduced-motion * {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
      
      .reduced-motion .service-card:hover,
      .reduced-motion .project-card:hover {
        transform: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  optimizeImages() {
    // Reduce image quality for low-end devices
    const images = document.querySelectorAll('img');
    images.forEach(img => {
      if (img.src.includes('high-quality')) {
        img.src = img.src.replace('high-quality', 'low-quality');
      }
    });
  }

  reduceComplexity() {
    // Reduce grid complexity for better performance
    const grids = document.querySelectorAll('.services-grid, .projects-grid, .blog-posts');
    grids.forEach(grid => {
      if (grid.children.length > 6) {
        grid.style.gridTemplateColumns = 'repeat(auto-fit, minmax(280px, 1fr))';
      }
    });
  }

  getMetrics() {
    return {
      ...this.metrics,
      timestamp: Date.now(),
      isMobile: this.isMobile,
      isLowEndDevice: this.isLowEndDevice
    };
  }

  logMetrics() {
    if (!this.isLowEndDevice) {
      console.group('Performance Metrics');
      console.table(this.metrics);
      console.groupEnd();
    }
  }

  // Mobile-specific optimizations
  optimizeMobileExperience() {
    if (this.isMobile) {
      // Optimize touch interactions
      this.optimizeTouchTargets();
      
      // Reduce viewport complexity
      this.optimizeViewport();
      
      // Optimize scrolling
      this.optimizeScrolling();
    }
  }

  optimizeTouchTargets() {
    // Ensure minimum touch target size (44px)
    const touchElements = document.querySelectorAll('button, a, input, select, textarea');
    touchElements.forEach(element => {
      const rect = element.getBoundingClientRect();
      if (rect.width < 44 || rect.height < 44) {
        element.style.minWidth = '44px';
        element.style.minHeight = '44px';
        element.style.padding = '12px';
      }
    });
  }

  optimizeViewport() {
    // Optimize viewport for mobile
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes');
    }
  }

  optimizeScrolling() {
    // Optimize scrolling performance
    document.documentElement.style.scrollBehavior = 'auto';
    
    // Add smooth scrolling only for non-low-end devices
    if (!this.isLowEndDevice) {
      document.documentElement.style.scrollBehavior = 'smooth';
    }
  }

  // Performance recommendations
  getRecommendations() {
    const recommendations = [];
    
    if (this.metrics.pageLoad > 3000) {
      recommendations.push('Page load time is slow. Consider optimizing images and reducing JavaScript bundle size.');
    }
    
    if (this.metrics.cumulativeLayoutShift > 0.1) {
      recommendations.push('High layout shift detected. Consider setting explicit dimensions for images and avoiding dynamic content insertion.');
    }
    
    if (this.isLowEndDevice) {
      recommendations.push('Device detected as low-end. Animations and effects have been reduced for better performance.');
    }
    
    return recommendations;
  }
}

// Create and export singleton instance
const performanceMonitor = new PerformanceMonitor();

// Auto-optimize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    performanceMonitor.optimizeForDevice();
    performanceMonitor.optimizeMobileExperience();
  });
} else {
  performanceMonitor.optimizeForDevice();
  performanceMonitor.optimizeMobileExperience();
}

export default performanceMonitor;
